import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback
} from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import Text from "@/components/ui/text";
import { DrawerFooter } from "@/components/ui/drawer";
import FormNew from "@/components/controls/Form/FormNew";
import AddActionButton from "@/components/ui/add-action-button";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FormFieldNewType } from "@/components/controls/Form/types";
import { AddFieldProps } from "../../../FieldManagement/types";
import { ADD_CUSTOM_FIELD_TRANSLATION_KEY } from "../../../FieldManagement/const";
import { useFieldForm } from "../../../FieldManagement/hooks/useFieldForm";
import { useFieldConfigurationData } from "../../../FieldManagement/hooks/useFieldConfigurationData";
import {
  getFieldDisplayTypeIcon,
  getFieldDisplayTypeLabel
} from "../../../FieldManagement/hooks";
import { FieldDisplayType } from "../../../FieldManagement/types";
import {
  generateFieldDisplayTypeOptions,
  getFieldDisplayTypeHelpText
} from "../../../FieldManagement/utils";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { FILE_FIELD_ANY_TYPE } from "@/utils/entityFieldsUtils/fileFieldUtils";
import { WorkflowRequiredFieldSection } from "./WorkflowRequiredFieldSection";
import { AIAutofillSection } from "./AIAutofillSection";
import { AUTOFILL_TRANSLATION_KEY } from "../const";
import {
  DreamerFlow,
  useDeleteAutofillDraftQuestion
} from "../hooks/useAutofillApi";

interface WorkflowCustomFieldProps extends AddFieldProps {
  questionnaireId: string;
  enableAutofill?: boolean;
  onRequiredForPassChange?: (required: boolean) => void;
  forceChecklistItem?: boolean;
}

export const WorkflowCustomField: React.FC<WorkflowCustomFieldProps> = ({
  questionnaireId,
  enableAutofill = true,
  onBack,
  onCancel,
  onSave,
  onRequiredForPassChange,
  forceChecklistItem
}) => {
  const { t } = useTranslation();
  const [isRequiredForPass, setIsRequiredForPass] = useState(false);
  const [filledByDreamer, setFilledByDreamer] = useState(false);
  const [autofillInstructions, setAutofillInstructions] = useState<
    string | null
  >(null);
  const [autofillQuestionId, setAutofillQuestionId] = useState<string | null>(
    null
  );
  const [dreamerFlow, setDreamerFlow] = useState<DreamerFlow | null>(null);
  const [autofillSaveBlocked, setAutofillSaveBlocked] = useState(false);
  const [autofillBlockTooltip, setAutofillBlockTooltip] = useState<
    string | null
  >(null);
  const savedRef = useRef(false);
  const deleteDraft = useDeleteAutofillDraftQuestion();
  const { enabled: isFileUploadEnabled } = useFeatureFlag(
    FeatureFlags.CRM_9188_FILE_FIELD_MGMT
  );

  const {
    formState,
    isFormValid,
    formRef,
    formSetValue,
    setFormSetValue,
    setShowHelpText,
    handleFormValid,
    handleOptionsManagerChange,
    handleSubmitButton
  } = useFieldForm({
    fieldData: null,
    isEditMode: false
  });

  // Dreamer cannot produce a file, so a file-upload question has no autofill
  // to offer — the section is withheld rather than left to save autofill
  // metadata the field can never act on.
  const showAutofill =
    enableAutofill &&
    formState.formData.fieldType !== FieldDisplayType.FILE_UPLOAD;

  const handleRequiredForPassChange = (required: boolean) => {
    setIsRequiredForPass(required);
    onRequiredForPassChange?.(required);
  };

  const questionIdRef = useRef<string | null>(null);
  questionIdRef.current = autofillQuestionId;

  // The mutation object is a new reference on every render, so it is read
  // through a ref rather than closed over by the memoized callbacks below.
  const deleteDraftRef = useRef(deleteDraft.mutate);
  useEffect(() => {
    deleteDraftRef.current = deleteDraft.mutate;
  }, [deleteDraft.mutate]);

  // Switching to file upload after configuring autofill has to discard what
  // was already set up, draft question included — hiding the section alone
  // would leave the state to be saved with the question.
  const clearAutofillState = useCallback(() => {
    if (questionIdRef.current) {
      deleteDraftRef.current({
        pathParams: { questionId: questionIdRef.current }
      });
    }
    setFilledByDreamer(false);
    setAutofillInstructions(null);
    setAutofillQuestionId(null);
    setDreamerFlow(null);
    setAutofillSaveBlocked(false);
    setAutofillBlockTooltip(null);
  }, []);

  // The drawer unmounts this on any close (cancel, back, or the header X), so
  // a single unmount cleanup covers every dismissal path without saving.
  useEffect(() => {
    return () => {
      if (questionIdRef.current && !savedRef.current) {
        deleteDraft.mutate({
          pathParams: { questionId: questionIdRef.current }
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (!isFormValid || autofillSaveBlocked) return;
    try {
      // Combine form data and options manager data for the question creation
      const combinedFieldData = {
        ...formState.formData,
        ...formState.optionsManagerData,
        isRequiredForPass,
        filledByDreamer,
        autofillInstructions: filledByDreamer ? autofillInstructions : null,
        autofillQuestionId: filledByDreamer ? autofillQuestionId : null,
        dreamerFlow: filledByDreamer ? dreamerFlow : null,
        isRequired: formState.isRequired
      };

      savedRef.current = true;
      onSave(combinedFieldData);
    } catch (error) {
      console.error("Failed to prepare workflow custom field data:", error);
    }
  };

  const fieldConfigurationData = useFieldConfigurationData({
    fieldType: formState.formData.fieldType,
    formData: formState.formData,
    optionsManagerData: formState.optionsManagerData,
    fieldData: null,
    formSetValue,
    onOptionsManagerChange: handleOptionsManagerChange,
    isPresetFieldEdit: false
  });

  const formSections = useMemo((): FormSectionProps[] => {
    const fieldTypeOptions = forceChecklistItem
      ? [
          {
            id: FieldDisplayType.CHECKLIST_ITEM,
            displayName: getFieldDisplayTypeLabel(
              FieldDisplayType.CHECKLIST_ITEM,
              t
            )
          }
        ]
      : generateFieldDisplayTypeOptions(t, true, true, false, isFileUploadEnabled);
    const selectedFieldDisplayType = formState.formData.fieldType;
    const { configFields, bottomCustomComponent } = fieldConfigurationData;

    const fieldTypeFields: FormFieldNewType[] = [
      {
        name: "fieldType",
        type: "singleSelect" as const,
        label: t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.fieldType`),
        required: true,
        disabled: forceChecklistItem,
        defaultValue: forceChecklistItem
          ? FieldDisplayType.CHECKLIST_ITEM
          : undefined,
        helpText: getFieldDisplayTypeHelpText(selectedFieldDisplayType, t),
        fieldActionProps: {
          fieldName: "fieldType",
          options: fieldTypeOptions,
          placeholder: t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.selectFieldType`),
          renderIcon: (value: string | number) =>
            getFieldDisplayTypeIcon(value as FieldDisplayType),
          onSelection: (value: string | number) => {
            // FormNew publishes its methods as an object; calling formSetValue
            // itself threw before any default below was written.
            const setFieldValue = formSetValue?.setValue;
            if (typeof setFieldValue === "function") {
              setFieldValue("fieldType", value, { shouldValidate: true });
              if (
                value === FieldDisplayType.SINGLE_SELECT ||
                value === FieldDisplayType.MULTI_SELECT
              ) {
                setFieldValue("options", [""]);
                setFieldValue("allowUsersToAdd", false);
                setFieldValue("defaultValue", "none");
              } else if (value === FieldDisplayType.CHECKBOX) {
                setFieldValue("defaultValue", "unchecked");
              } else if (value === FieldDisplayType.CURRENCY) {
                setFieldValue("defaultValue", "");
              } else if (value === FieldDisplayType.FILE_UPLOAD) {
                setFieldValue("supportedFiles", FILE_FIELD_ANY_TYPE, {
                  shouldValidate: true
                });
                setFieldValue("allowMultipleFiles", true);
                clearAutofillState();
              }
              // handleSave spreads the whole form data, so a type picked after
              // FILE_UPLOAD would carry file configuration it cannot use.
              if (value !== FieldDisplayType.FILE_UPLOAD) {
                setFieldValue("supportedFiles", undefined);
                setFieldValue("allowMultipleFiles", undefined);
              }
            }
          }
        }
      },
      ...configFields
    ];

    const fieldLabelFields: FormFieldNewType[] = [
      {
        name: "fieldLabel",
        type: "text" as const,
        label: t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.fieldLabel`),
        required: true,
        fieldActionProps: {
          placeholder: t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.addFieldLabel`)
        }
      }
    ];

    if (formState.showHelpText) {
      fieldLabelFields.push({
        name: "fieldHelpText",
        type: "text" as const,
        label: t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.fieldHelpText`),
        required: false,
        helpText: t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.addFieldHelpText`),
        fieldActionProps: {
          placeholder: ""
        }
      });
    }

    return [
      {
        id: "field-label",
        divider: true,

        dividerClassName: "my-2",
        fields: fieldLabelFields,
        bottomCustomComponent: !formState.showHelpText ? (
          <div className="flex justify-start mt-[-22px]">
            <AddActionButton
              label={t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.addHelpText`)}
              onClick={() => setShowHelpText(true)}
              textVariant="body"
            />
          </div>
        ) : undefined
      },
      {
        id: "field-type",
        divider: true,
        sectionClassName:
          fieldConfigurationData.configSection?.sectionClassName || "gap-6",
        fieldsContainerClassName:
          fieldConfigurationData.configSection?.fieldsContainerClassName,
        dividerClassName: "my-2",
        fields: fieldTypeFields,
        bottomCustomComponent
      }
    ];
  }, [
    formState,
    formSetValue,
    handleOptionsManagerChange,
    setShowHelpText,
    t,
    forceChecklistItem,
    isFileUploadEnabled,
    clearAutofillState
  ]);

  return (
    <>
      <div className="p-5 w-full overflow-y-auto max-h-[calc(100vh-200px)]">
        <FormNew
          key={forceChecklistItem ? "checklist" : "general"}
          sections={formSections}
          isFormValid={handleFormValid}
          onSubmit={handleSave}
          formRef={formRef}
          onSetValue={setFormSetValue}
        />

        {showAutofill ? (
          <>
            <div className="w-full border border-border-border1 my-4" />
            <AIAutofillSection
              fieldName={formState.formData.fieldLabel || ""}
              fieldType={formState.formData.fieldType || ""}
              helpText={formState.formData.fieldHelpText || ""}
              questionnaireId={questionnaireId}
              enabled={filledByDreamer}
              onEnabledChange={setFilledByDreamer}
              onInstructionsChange={setAutofillInstructions}
              onQuestionIdChange={setAutofillQuestionId}
              onDreamerFlowChange={setDreamerFlow}
              onSaveBlockedChange={(blocked, tooltip) => {
                setAutofillSaveBlocked(blocked);
                setAutofillBlockTooltip(tooltip ?? null);
              }}
              translationPrefix={AUTOFILL_TRANSLATION_KEY}
              className=""
            />
            <div className="w-full border border-border-border1 my-4" />
          </>
        ) : null}

        <WorkflowRequiredFieldSection
          isRequiredForPass={isRequiredForPass}
          onRequiredForPassChange={handleRequiredForPassChange}
          translationPrefix="settings.deals.workflow.requiredFieldSection"
          className={showAutofill ? "" : "mt-6"}
        />
      </div>

      <DrawerFooter>
        <div className="flex justify-between gap-2 w-full p-4 border-t-[0.5px] border-border-border1">
          <Button variant="tertiary" onClick={onBack} className="h-9">
            {t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.back`)}
          </Button>
          <div className="flex gap-2 ml-auto">
            <Button variant="secondaryNoBg" onClick={onCancel} className="h-9">
              {t("cancel")}
            </Button>
            {autofillBlockTooltip ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      tabIndex={0}
                      aria-disabled="true"
                      className="inline-flex cursor-not-allowed"
                    >
                      <Button disabled className="h-9">
                        {t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.addField`)}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <Text variant="smBody" className="max-w-xs">
                      {autofillBlockTooltip}
                    </Text>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Button
                onClick={handleSubmitButton}
                disabled={!isFormValid || autofillSaveBlocked}
                className="h-9"
              >
                {t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.addField`)}
              </Button>
            )}
          </div>
        </div>
      </DrawerFooter>
    </>
  );
};
